/**
 * Lista scontrini con filtri per anno, stato e tipo.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { Receipt } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlNumberState } from '@lib/useUrlState';
import { useFormatters } from '@lib/formatters';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

interface ScontrinoItem {
  id: string;
  year: number;
  tipo: string;
  stato: string;
  totale: string;
  numero_scontrino_fiscale: number | null;
  data_emissione: string | null;
  created_at: string;
}

const ScontriniList: React.FC = () => {
  const navigate = useNavigate();
  const fmt = useFormatters();
  const currentYear = new Date().getFullYear();

  const [scontrini, setScontrini] = useState<ScontrinoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [year, setYear] = useUrlNumberState('anno', currentYear);

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ year: String(year) });
      const res = await fetch(`/api/v1/cassa/scontrini?${params}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: ScontrinoItem[] = await res.json();
      setScontrini(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    void carica();
  }, [carica]);

  const formatDateTime = (iso: string): string => fmt.dateTime(iso);

  const formatCurrency = (value: string): string => fmt.currency(value);

  const statoVariant = (stato: string) => {
    switch (stato) {
      case 'chiuso':
        return 'secondary' as const;
      case 'annullato':
        return 'destructive' as const;
      default:
        return 'outline' as const;
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Scontrini
        </h1>
        <div className="flex items-center gap-3">
          <label className="text-sm text-muted-foreground">Anno:</label>
          <Input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="w-24"
          />
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Scontrini {year}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && (
            <p className="p-6 text-center text-sm text-muted-foreground">Caricamento…</p>
          )}
          {!loading && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N. Fiscale</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  <TableHead>Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scontrini.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                      Nessuno scontrino trovato
                    </TableCell>
                  </TableRow>
                )}
                {scontrini.map((s: any) => (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/cassa/scontrini/${s.id}?year=${s.year}`)}
                  >
                    <TableCell className="font-mono text-sm">
                      {s.numero_scontrino_fiscale ?? '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{s.tipo}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statoVariant(s.stato)}>{s.stato}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(s.totale)}
                    </TableCell>
                    <TableCell className="text-sm">{formatDateTime(s.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ScontriniList;
